import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Header, Footer } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { CheckCircle2, MessageSquare, Star, Heart, Loader2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const TIPOS_TRATAMIENTO = [
  { id: 'residencial', label: 'Residencial (internación)' },
  { id: 'ambulatorio', label: 'Ambulatorio' },
  { id: 'online', label: 'Online' },
  { id: 'profesional', label: 'Profesional individual (psicólogo/psiquiatra)' },
];

export default function EncuestaPage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [survey, setSurvey] = useState(null);
  const [error, setError] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Form state
  const [recibio, setRecibio] = useState('');
  const [tipos, setTipos] = useState([]);
  const [centro, setCentro] = useState('');
  const [recomendacion, setRecomendacion] = useState('');
  const [nps, setNps] = useState(null);
  const [tiempoRespuesta, setTiempoRespuesta] = useState('');
  const [facilidad, setFacilidad] = useState('');
  const [mejora, setMejora] = useState('');
  const [publicar, setPublicar] = useState(false);
  const [nombrePublicar, setNombrePublicar] = useState('primer_nombre');

  useEffect(() => {
    const load = async () => {
      try {
        const r = await axios.get(`${API}/survey/token/${token}`);
        setSurvey(r.data);
        if (r.data.already_submitted) {
          setSubmitted(true);
        }
      } catch (e) {
        setError(e.response?.data?.detail || 'No pudimos cargar la encuesta.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token]);

  const toggleTipo = (id) => {
    setTipos((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!recibio) {
      toast.error('Cuéntanos si recibiste cotizaciones');
      return;
    }
    if (recomendacion.trim().length < 5) {
      toast.error('Por favor deja tu opinión o recomendación (mín. 5 caracteres)');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/survey/submit`, {
        token,
        recibio_cotizaciones: recibio,
        tipos_tratamiento: tipos,
        centro_contratado: centro || null,
        recomendacion,
        nivel_recomendacion: nps,
        tiempo_respuesta_centros: tiempoRespuesta || null,
        proceso_facil: facilidad || null,
        mejora_sugerencia: mejora || null,
        publicar_testimonio: publicar,
        nombre_publicar: publicar ? nombrePublicar : null,
      });
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error enviando la encuesta');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-teal-400 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-100">
        <Header />
        <div className="max-w-xl mx-auto px-4 py-24 text-center" data-testid="encuesta-error">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Enlace no válido</h1>
          <p className="text-slate-400 mb-6">{error}</p>
          <Link to="/">
            <Button className="bg-teal-600 hover:bg-teal-700">Volver al inicio</Button>
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-100">
        <Header />
        <div className="max-w-xl mx-auto px-4 py-20 text-center" data-testid="encuesta-thanks">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-teal-500/20 border border-teal-500/40 rounded-full mb-6">
            <CheckCircle2 className="w-8 h-8 text-teal-400" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-4">¡Gracias por tu tiempo!</h1>
          <p className="text-slate-300 leading-relaxed mb-8">
            Tu respuesta nos ayuda a mejorar el directorio y guía a otras familias que buscan ayuda.
            {publicar && ' Tu testimonio quedó pendiente de revisión — te avisaremos cuando esté publicado.'}
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Link to="/testimonios">
              <Button variant="outline" className="border-slate-700 text-slate-200 hover:bg-slate-800">
                Ver testimonios de otras familias
              </Button>
            </Link>
            <Link to="/">
              <Button className="bg-teal-600 hover:bg-teal-700">Volver al inicio</Button>
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="encuesta-page">
      <title>Cuéntanos cómo te fue · SinAdicciones.org</title>
      <meta name="robots" content="noindex,nofollow" />
      <Header />

      <section className="relative border-b border-slate-800/60 bg-gradient-to-br from-teal-900/20 via-slate-950 to-slate-950">
        <div className="max-w-3xl mx-auto px-4 pt-20 pb-10 md:pt-24 md:pb-14">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-teal-400 font-semibold mb-4">
            <MessageSquare className="w-4 h-4" /> Encuesta · 3 minutos
          </div>
          <h1 className="text-3xl md:text-5xl font-bold tracking-tight text-white leading-tight mb-4">
            Hola {survey?.name?.split(' ')[0] || ''}, cuéntanos cómo te fue
          </h1>
          <p className="text-slate-300 text-base md:text-lg leading-relaxed">
            Tu experiencia nos ayuda a exigir mejor servicio a los centros y a orientar a las próximas familias. Todo confidencial — nada se publica sin tu autorización explícita.
          </p>
        </div>
      </section>

      <form onSubmit={handleSubmit} className="max-w-3xl mx-auto px-4 py-12 space-y-8" data-testid="encuesta-form">

        {/* 1. Recibió cotizaciones */}
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-6 space-y-4">
            <Label className="text-white text-base font-semibold">1. ¿Recibiste cotizaciones de los centros/profesionales? <span className="text-red-400">*</span></Label>
            <RadioGroup value={recibio} onValueChange={setRecibio} className="space-y-2">
              {[
                { v: 'si', l: 'Sí, todos me contactaron' },
                { v: 'algunas', l: 'Algunas nomás me respondieron' },
                { v: 'no', l: 'No, no me contactó nadie' },
              ].map((o) => (
                <div key={o.v} className="flex items-center gap-3">
                  <RadioGroupItem value={o.v} id={`recibio-${o.v}`} data-testid={`recibio-${o.v}`} />
                  <Label htmlFor={`recibio-${o.v}`} className="text-slate-200 cursor-pointer font-normal">{o.l}</Label>
                </div>
              ))}
            </RadioGroup>
          </CardContent>
        </Card>

        {/* 2. Tipos de tratamiento */}
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-6 space-y-4">
            <Label className="text-white text-base font-semibold">2. ¿Qué tipo(s) de tratamiento buscabas?</Label>
            <div className="grid sm:grid-cols-2 gap-3">
              {TIPOS_TRATAMIENTO.map((t) => (
                <label key={t.id} className="flex items-center gap-3 p-3 border border-slate-800 rounded-lg hover:bg-slate-800/50 cursor-pointer">
                  <Checkbox
                    checked={tipos.includes(t.id)}
                    onCheckedChange={() => toggleTipo(t.id)}
                    data-testid={`tipo-${t.id}`}
                  />
                  <span className="text-slate-200 text-sm">{t.label}</span>
                </label>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* 3. Centro contratado */}
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-6 space-y-4">
            <Label htmlFor="centro" className="text-white text-base font-semibold">3. ¿Contrataste algún centro o profesional?</Label>
            <p className="text-slate-400 text-sm">Escribe el nombre si te acuerdas (opcional).</p>
            <Input
              id="centro"
              value={centro}
              onChange={(e) => setCentro(e.target.value)}
              placeholder="Ej: Centro Los Andes, Dr. Juan Pérez, etc."
              className="bg-slate-950 border-slate-700 text-white"
              data-testid="input-centro"
            />
          </CardContent>
        </Card>

        {/* 4. Tiempo de respuesta */}
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-6 space-y-4">
            <Label className="text-white text-base font-semibold">4. ¿Cuánto tardaron en contactarte?</Label>
            <RadioGroup value={tiempoRespuesta} onValueChange={setTiempoRespuesta} className="space-y-2">
              {[
                { v: 'menos_24h', l: 'Menos de 24 horas' },
                { v: '1_3_dias', l: 'Entre 1 y 3 días' },
                { v: 'mas_3_dias', l: 'Más de 3 días' },
                { v: 'no_respondieron', l: 'No me respondieron' },
              ].map((o) => (
                <div key={o.v} className="flex items-center gap-3">
                  <RadioGroupItem value={o.v} id={`tr-${o.v}`} data-testid={`tiempo-${o.v}`} />
                  <Label htmlFor={`tr-${o.v}`} className="text-slate-200 cursor-pointer font-normal">{o.l}</Label>
                </div>
              ))}
            </RadioGroup>
          </CardContent>
        </Card>

        {/* 5. Facilidad del proceso */}
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-6 space-y-4">
            <Label className="text-white text-base font-semibold">5. ¿Cómo te pareció el proceso de comparar centros?</Label>
            <RadioGroup value={facilidad} onValueChange={setFacilidad} className="space-y-2">
              {[
                { v: 'muy_facil', l: 'Muy fácil' },
                { v: 'facil', l: 'Fácil' },
                { v: 'regular', l: 'Regular' },
                { v: 'dificil', l: 'Difícil' },
              ].map((o) => (
                <div key={o.v} className="flex items-center gap-3">
                  <RadioGroupItem value={o.v} id={`f-${o.v}`} data-testid={`facilidad-${o.v}`} />
                  <Label htmlFor={`f-${o.v}`} className="text-slate-200 cursor-pointer font-normal">{o.l}</Label>
                </div>
              ))}
            </RadioGroup>
          </CardContent>
        </Card>

        {/* 6. NPS */}
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-6 space-y-4">
            <Label className="text-white text-base font-semibold">6. Del 1 al 10, ¿qué tan probable es que recomiendes SinAdicciones.org a otra familia?</Label>
            <div className="flex flex-wrap gap-2">
              {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setNps(n)}
                  className={`w-11 h-11 rounded-lg font-bold text-sm transition-colors ${
                    nps === n
                      ? 'bg-teal-500 text-white border-2 border-teal-400'
                      : 'bg-slate-800 text-slate-300 border-2 border-transparent hover:bg-slate-700'
                  }`}
                  data-testid={`nps-${n}`}
                >
                  {n}
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* 7. Recomendación / opinión */}
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-6 space-y-4">
            <Label htmlFor="recomendacion" className="text-white text-base font-semibold">7. Tu opinión o recomendación <span className="text-red-400">*</span></Label>
            <p className="text-slate-400 text-sm">Cuéntanos con tus palabras cómo fue tu experiencia. Esto es lo más valioso para otras familias.</p>
            <Textarea
              id="recomendacion"
              value={recomendacion}
              onChange={(e) => setRecomendacion(e.target.value)}
              rows={6}
              placeholder="Ej: Recibí cotizaciones de 3 centros. Uno respondió al toque por WhatsApp, terminamos contratando un tratamiento ambulatorio y estamos muy contentos con el equipo..."
              className="bg-slate-950 border-slate-700 text-white"
              data-testid="input-recomendacion"
            />
          </CardContent>
        </Card>

        {/* 8. Mejora */}
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-6 space-y-4">
            <Label htmlFor="mejora" className="text-white text-base font-semibold">8. ¿Qué mejorarías del proceso?</Label>
            <Textarea
              id="mejora"
              value={mejora}
              onChange={(e) => setMejora(e.target.value)}
              rows={3}
              placeholder="Ej: Que sea más rápida la respuesta de los centros, tener rango de precios más claro..."
              className="bg-slate-950 border-slate-700 text-white"
              data-testid="input-mejora"
            />
          </CardContent>
        </Card>

        {/* 9. Testimonio opt-in */}
        <Card className="bg-teal-900/20 border-teal-500/30" data-testid="testimonio-optin-card">
          <CardContent className="p-6 space-y-4">
            <div className="flex items-start gap-3">
              <Heart className="w-6 h-6 text-teal-400 mt-0.5 flex-shrink-0" />
              <div>
                <Label className="text-white text-base font-semibold">¿Quieres compartir tu experiencia con otras familias?</Label>
                <p className="text-slate-300 text-sm mt-2 leading-relaxed">
                  Podemos publicar tu respuesta como testimonio en <Link to="/testimonios" className="text-teal-400 hover:underline" target="_blank" rel="noreferrer">sinadicciones.org/testimonios</Link>. Nada se publica sin tu autorización y siempre pasa por moderación antes.
                </p>
              </div>
            </div>
            <label className="flex items-center gap-3 p-3 bg-slate-950/50 rounded-lg cursor-pointer">
              <Checkbox
                checked={publicar}
                onCheckedChange={setPublicar}
                data-testid="publicar-testimonio"
              />
              <span className="text-slate-200 font-medium text-sm">Sí, autorizo publicar mi opinión como testimonio</span>
            </label>

            {publicar && (
              <div className="pl-4 border-l-2 border-teal-500/40 space-y-3" data-testid="nombre-publicar-block">
                <Label className="text-slate-200 text-sm">¿Cómo prefieres que aparezcas?</Label>
                <RadioGroup value={nombrePublicar} onValueChange={setNombrePublicar} className="space-y-2">
                  <div className="flex items-center gap-3">
                    <RadioGroupItem value="completo" id="np-completo" data-testid="np-completo" />
                    <Label htmlFor="np-completo" className="text-slate-200 font-normal cursor-pointer">Nombre completo (ej: María González)</Label>
                  </div>
                  <div className="flex items-center gap-3">
                    <RadioGroupItem value="primer_nombre" id="np-primer" data-testid="np-primer" />
                    <Label htmlFor="np-primer" className="text-slate-200 font-normal cursor-pointer">Solo primer nombre + región (ej: María, RM)</Label>
                  </div>
                  <div className="flex items-center gap-3">
                    <RadioGroupItem value="anonimo" id="np-anon" data-testid="np-anon" />
                    <Label htmlFor="np-anon" className="text-slate-200 font-normal cursor-pointer">Iniciales anónimas (ej: M.G.)</Label>
                  </div>
                </RadioGroup>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Submit */}
        <div className="flex justify-end pt-2">
          <Button
            type="submit"
            disabled={submitting}
            className="bg-teal-600 hover:bg-teal-700 text-white font-semibold px-8 py-6 rounded-lg text-base"
            data-testid="submit-encuesta"
          >
            {submitting ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Enviando…</>
            ) : (
              <><Star className="w-4 h-4 mr-2" /> Enviar mi respuesta</>
            )}
          </Button>
        </div>
      </form>

      <Footer />
    </div>
  );
}
